import { Service } from './service'
import apiHelper from '@utils/cunstomInterseptor'
import { isNetworkToken } from '@utils/consts'

export const WITHDRAW_MIN_LIMIT_ASSET_CONFIG = {
  USDTTRC20: { atomicDivider: 1000000 },
  USDTERC20: { atomicDivider: 1000000 },
  USDTBEP20: { atomicDivider: 1000000 }
}

const DEFAULT_NETWORK_TOKEN_LIMIT_CONFIG = {
  atomicDivider: 1000000,
  decimalPlaces: 6,
  depositFallbackToMinimalUnit: true
}

export const ASSET_LIMIT_CONFIG = {
  // asset_settings limits for BTC are in the same micro units as every other
  // asset (1 micro = 1e-6 BTC = 100 sat), not in satoshi; only the display
  // precision is 8 decimals
  BTC: { atomicDivider: 1000000, decimalPlaces: 8, withdrawalUsesDefaultMinAmount: false },
  USDTTRC20: { ...DEFAULT_NETWORK_TOKEN_LIMIT_CONFIG, withdrawalUsesDefaultMinAmount: false }
}

function limitConfigFor (asset) {
  if (asset && ASSET_LIMIT_CONFIG[asset]) return ASSET_LIMIT_CONFIG[asset]
  return isNetworkToken(asset) ? DEFAULT_NETWORK_TOKEN_LIMIT_CONFIG : null
}

const MIN_LIMIT_FIELDS = {
  deposit: [
    'deposit_min_limit'
  ],
  withdrawal: [
    'withdrawal_min_limit'
  ]
}

export class LimitsService extends Service {
  async getMinimumLimits (asset = 'USDTERC20') {
    try {
      return await apiHelper.get('/asset_settings', { asset })
    } catch (err) {
      const responseData = err && err.response ? err.response.data : null
      const message = typeof responseData === 'string' && responseData.trim()
        ? responseData
        : (err && err.message) || 'Failed to load asset settings'

      console.error('Error loading asset settings:', message)

      return {
        data: null,
        error: {
          message,
          status: err && err.response ? err.response.status : null
        }
      }
    }
  }

  isSupportedMinLimitAsset (asset) {
    return !!limitConfigFor(asset)
  }

  usesDefaultWithdrawalMinAmount (asset) {
    const config = limitConfigFor(asset)
    return !config || config.withdrawalUsesDefaultMinAmount !== false
  }

  normalizeMinAmountByAsset (value, asset, isAtomic = false) {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return null

    const config = limitConfigFor(asset)
    if (isAtomic && config && Number.isInteger(numericValue)) {
      return numericValue / config.atomicDivider
    }

    return numericValue
  }

  getMinAmountFromAssetSettings (data, asset, type = 'withdrawal') {
    if (!data || typeof data !== 'object') return null

    const fields = MIN_LIMIT_FIELDS[type] || []
    for (const field of fields) {
      if (data[field] !== undefined && data[field] !== null && data[field] !== '') {
        return this.normalizeMinAmountByAsset(data[field], asset, true)
      }
    }

    return null
  }

  isAssetSettingsNotFoundError (error) {
    if (!error) return false

    const message = (error.message || '').toString().toLowerCase()
    return error.status === 404 || message.indexOf('asset settings not found') !== -1
  }

  formatMinAmount (value, asset) {
    // Number(null/'') === 0, which would fake a '0.00' minimum — treat absent as absent
    if (value === null || value === undefined || value === '') return null
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return null

    const config = limitConfigFor(asset)
    const fixed = numericValue.toFixed(config ? config.decimalPlaces : 6)
    const normalized = fixed.replace(/\.?0+$/, '')
    const parts = normalized.split('.')
    const integerPart = parts[0]
    const decimalPart = parts[1] || ''

    if (!decimalPart.length) return `${integerPart}.00`
    if (decimalPart.length === 1) return `${integerPart}.${decimalPart}0`

    return `${integerPart}.${decimalPart}`
  }

  getAssetMinimalUnit (asset) {
    const config = limitConfigFor(asset)
    if (!config || !config.atomicDivider) return null

    return this.formatMinAmount(1 / config.atomicDivider, asset)
  }

  getDepositFallback (asset) {
    const config = limitConfigFor(asset)
    return config && config.depositFallbackToMinimalUnit ? this.getAssetMinimalUnit(asset) : null
  }

  // Shared, memoized deposit min-amount loader (used by both topup views).
  // Resolves to a formatted amount or null. Successful and 404 ("no settings")
  // results are cached; transient errors use the asset's configured fallback
  // and are retried on the next call. In-flight requests are deduplicated.
  loadDepositMinAmount (asset) {
    if (!this.isSupportedMinLimitAsset(asset)) return Promise.resolve(null)
    if (!this._depositMinAmounts) {
      this._depositMinAmounts = {}
      this._depositMinAmountRequests = {}
    }
    if (asset in this._depositMinAmounts) return Promise.resolve(this._depositMinAmounts[asset])
    if (this._depositMinAmountRequests[asset]) return this._depositMinAmountRequests[asset]

    const request = (async () => {
      try {
        const response = await this.getMinimumLimits(asset)
        if (response && response.error) {
          const fallback = this.getDepositFallback(asset)
          if (this.isAssetSettingsNotFoundError(response.error)) {
            this._depositMinAmounts[asset] = fallback
          }
          return fallback
        }

        const minAmount = this.getMinAmountFromAssetSettings(response && response.data, asset, 'deposit')
        const formatted = this.formatMinAmount(minAmount, asset) || this.getDepositFallback(asset)
        this._depositMinAmounts[asset] = formatted
        return formatted
      } finally {
        delete this._depositMinAmountRequests[asset]
      }
    })()
    this._depositMinAmountRequests[asset] = request
    return request
  }

  // Withdrawal counterpart of loadDepositMinAmount. An absent setting means
  // no minimum for assets supported here. Transient errors are retried.
  loadWithdrawalMinAmount (asset) {
    if (!this.isSupportedMinLimitAsset(asset)) return Promise.resolve(null)
    if (!this._withdrawalMinAmounts) {
      this._withdrawalMinAmounts = {}
      this._withdrawalMinAmountRequests = {}
    }
    if (asset in this._withdrawalMinAmounts) return Promise.resolve(this._withdrawalMinAmounts[asset])
    if (this._withdrawalMinAmountRequests[asset]) return this._withdrawalMinAmountRequests[asset]

    const request = (async () => {
      try {
        const response = await this.getMinimumLimits(asset)
        if (response && response.error) {
          if (this.isAssetSettingsNotFoundError(response.error)) this._withdrawalMinAmounts[asset] = null
          return null
        }

        const minAmount = this.getMinAmountFromAssetSettings(response && response.data, asset, 'withdrawal')
        const formatted = this.formatMinAmount(minAmount, asset)
        this._withdrawalMinAmounts[asset] = formatted
        return formatted
      } finally {
        delete this._withdrawalMinAmountRequests[asset]
      }
    })()
    this._withdrawalMinAmountRequests[asset] = request
    return request
  }
}

export const limitsService = new LimitsService()



// WEBPACK FOOTER //
// ./src/js/services/limits.service.js